/** Standard grid controls — THE toolbar for every data grid in the console
 *  (see aidocs/context/components.md, "Grid toolbar rule").
 *
 *  Right-aligned icon buttons: refresh + auto-refresh period menu (when the
 *  page supplies the `refresh` config — every grid does; see
 *  GridRefreshControl), the presets dropdown (when the page supplies
 *  the `presets` config; the applied preset's name chip pins to the LEFT of
 *  the row), manage columns, filter panel, reset filters (when the page
 *  supplies a handler), CSV export of the loaded rows, and — for grids whose
 *  backend supports a generic free-text `search` param — a search icon that
 *  expands into a text field (mui.com real-time demo style). Pivot is
 *  deliberately absent: it requires the MUI X Premium license/package; the
 *  project licenses Pro.
 *
 *  Search modes:
 *  - default: drives the grid quick filter (ResourceDataGrid maps
 *    `quickFilterValues` → the DRF `search` param);
 *  - external (`onSearchChange` given): controls page-owned search state —
 *    for grids that don't route search through the filter model (e.g. the
 *    tour-operators tree).
 */
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import CancelIcon from '@mui/icons-material/Cancel';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Box, Chip, Divider, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import {
  GridPreferencePanelsValue,
  gridQuickFilterValuesSelector,
  useGridApiContext,
} from '@mui/x-data-grid-pro';
import { useEffect, useRef, useState } from 'react';

import type { PresetsToolbarConfig } from '@/hooks/useGridPresets';

import { BatchActionsMenu, type BatchToolbarState } from './BatchActionsMenu';
import { CountModeMenu, type CountModeToolbarState } from './CountModeMenu';
import { GridRefreshControl, type GridRefreshToolbarState } from './GridRefreshControl';
import { PresetsMenu } from './PresetsMenu';
import { SelectAllBanner } from './SelectAllBanner';

export interface GridControlsConfig {
  /** Batch-actions menu state (ResourceDataGrid supplies it when the page
   *  configures `batchActions`). Renders a left-pinned "Batch actions" button
   *  only while a selection is active. */
  batch?: BatchToolbarState;
  /** Clears page-level (URL/toolbar) filters; the button also clears the
   *  grid's own filter model + quick filter. Button hidden when omitted. */
  onResetAllFilters?: () => void;
  resetDisabled?: boolean;
  /** Enable the expanding free-text search. Only set this on grids whose
   *  backend supports a generic `search` param — never for grids where all
   *  filtering is column-specific. */
  search?: boolean;
  searchPlaceholder?: string;
  /** External search mode (see module docs). */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /** With `onSearchChange`: fire only on Enter / clear instead of per
   *  keystroke — for expensive submit-style searches (e.g. the sessions
   *  token/fingerprint lookup). */
  searchOnSubmit?: boolean;
  /** Hide the filter-panel button (grids with `disableColumnFilter`). */
  hideFilterButton?: boolean;
  /** CSV file name (".csv" appended by the grid). */
  exportFileName?: string;
  /** Grid presets dropdown (useGridPresets().toolbar). Button hidden when
   *  omitted — only preset-wired pages show it. */
  presets?: PresetsToolbarConfig;
  /** Row-total mode switch (useCountMode().mode/setMode). Button hidden when
   *  omitted, so grids that always count show no new control. */
  countMode?: CountModeToolbarState;
  /** Refresh now + auto-refresh period menu (`gridRefreshToolbar()` builds it;
   *  ResourceDataGrid supplies it itself). First in the right-hand cluster, so
   *  when its countdown label appears no other icon moves. */
  refresh?: GridRefreshToolbarState;
}

const SEARCH_DEBOUNCE_MS = 350;
const SEARCH_WIDTH = 240;

export function GridControlsToolbar({
  batch,
  onResetAllFilters,
  resetDisabled,
  search,
  searchPlaceholder = 'Search…',
  searchValue,
  onSearchChange,
  searchOnSubmit,
  hideFilterButton,
  exportFileName = 'export',
  presets,
  countMode,
  refresh,
}: GridControlsConfig) {
  const apiRef = useGridApiContext();
  const external = onSearchChange !== undefined;
  const [input, setInput] = useState(searchValue ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  // Controlled external value changed from outside (page reset) → follow it.
  useEffect(() => {
    if (external && !searchOnSubmit && searchValue !== undefined && searchValue !== input) {
      setInput(searchValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  // `/` focuses the (visible) grid search when nothing else is being typed in.
  useEffect(() => {
    if (!search) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const active = document.activeElement as HTMLElement | null;
      if (
        active &&
        (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)
      )
        return;
      if (inputRef.current?.offsetParent == null) return; // hidden (inactive tab)
      e.preventDefault();
      inputRef.current.focus();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [search]);

  // Internal mode: debounce keystrokes into the grid quick filter (which
  // ResourceDataGrid translates into the server `search` param). Only a
  // CHANGED value is written: this effect also runs on mount with an empty
  // input, and pushing `[]` into a model that has no `quickFilterValues` is
  // still a model change to the grid — it re-emitted onFilterModelChange
  // 350ms after every mount, which re-keyed the list query (search
  // undefined → '') for a duplicate fetch and, in preset mode, diverged the
  // preset with no user action.
  useEffect(() => {
    if (!search || external) return;
    const t = window.setTimeout(() => {
      const trimmed = input.trim();
      const next = trimmed ? [trimmed] : [];
      const current = gridQuickFilterValuesSelector(apiRef) ?? [];
      if (current.join(' ') === next.join(' ')) return;
      apiRef.current.setQuickFilterValues(next);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, search, external]);

  // Internal mode: an external reset (the empty-state "Clear filters" button,
  // page code) empties the quick filter — mirror the non-empty → empty
  // transition into the local input so the field doesn't show stale text.
  const hadQuickRef = useRef(false);
  useEffect(() => {
    if (!search || external) return undefined;
    return apiRef.current.subscribeEvent('filterModelChange', (model) => {
      const has = Boolean(model.quickFilterValues?.length);
      if (!has && hadQuickRef.current) setInput('');
      hadQuickRef.current = has;
    });
  }, [search, external, apiRef]);

  const changeSearch = (v: string) => {
    setInput(v);
    // Submit mode: only an explicit clear propagates immediately.
    if (external && (!searchOnSubmit || v === '')) onSearchChange(v);
  };

  const handleReset = () => {
    apiRef.current.setFilterModel({ items: [], quickFilterValues: [] });
    setInput('');
    if (external) onSearchChange('');
    onResetAllFilters?.();
  };

  // On a narrow grid (390 px) the row wraps rather than squeeze: the selection group, the
  // icon cluster and the search each move to a line of their own, right-aligned, so nothing
  // overlaps and nothing scrolls sideways. At desktop widths everything fits one line.
  return (
    <Box
      data-grid-toolbar
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 0.5,
        px: 0.5,
        py: 0.25,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {/* Batch actions + inline select-all summary — pinned LEFT (before the
          preset chip). The summary lives in the toolbar row (not a bar above the
          grid) so engaging a selection never shifts the grid down. When no preset
          chip is present this carries the `mr: auto` that pushes the icon cluster
          right; when a chip is present the chip's own `mr: auto` does that. It
          wraps whole, and its parts wrap among themselves when even a line of its
          own is too narrow. */}
      {batch && (
        <Box
          data-grid-selection
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 0,
            maxWidth: '100%',
            ...(presets?.active ? {} : { mr: 'auto' }),
          }}
        >
          <BatchActionsMenu batch={batch} />
          <SelectAllBanner
            selectedCount={batch.count}
            totalCount={batch.matched}
            allMatching={batch.allMatching}
            onSelectAllMatching={batch.onSelectAllMatching}
            onClear={batch.clearSelection}
          />
        </Box>
      )}
      {/* The applied preset's name, pinned to the LEFT of the toolbar row
          (mr: auto in a flex-end container). "(modified)" = the view diverged
          from the preset and the changes are unsaved; the ✕ unloads the
          preset back to the default view. */}
      {presets?.active && (
        <Tooltip
          title={
            presets.active.modified
              ? `Based on preset #${presets.active.id} — the view has unsaved changes`
              : `Preset #${presets.active.id}`
          }
        >
          <Chip
            size="small"
            color="primary"
            variant={presets.active.modified ? 'outlined' : 'filled'}
            icon={<BookmarksIcon />}
            label={
              presets.active.modified ? `${presets.active.name} (modified)` : presets.active.name
            }
            onDelete={presets.clearPreset}
            deleteIcon={<CancelIcon aria-label="Unload preset" />}
            sx={{ mr: 'auto', maxWidth: 300 }}
          />
        </Tooltip>
      )}
      {refresh && <GridRefreshControl {...refresh} />}
      {presets && <PresetsMenu presets={presets} />}
      {countMode && <CountModeMenu {...countMode} />}
      <Tooltip title="Manage columns">
        <IconButton
          size="small"
          onClick={() => apiRef.current.showPreferences(GridPreferencePanelsValue.columns)}
        >
          <ViewColumnIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {!hideFilterButton && (
        <Tooltip title="Filters">
          <IconButton size="small" onClick={() => apiRef.current.showFilterPanel()}>
            <FilterListIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onResetAllFilters && (
        <Tooltip title="Reset filters">
          {/* span: a disabled button can't anchor a tooltip */}
          <span>
            <IconButton size="small" onClick={handleReset} disabled={resetDisabled}>
              <FilterAltOffIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip title="Export loaded rows (CSV)">
        <IconButton
          size="small"
          onClick={() => apiRef.current.exportDataAsCsv({ fileName: exportFileName, utf8WithBom: true })}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {search && (
        // The divider and the field wrap together: a line never ends in a lone divider.
        <Box data-grid-search sx={{ display: 'flex', alignItems: 'center', maxWidth: '100%' }}>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
          {/* Always visible — the most-used control on a grid shouldn't hide
              behind an icon click. `/` focuses it, Escape clears it. */}
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={input}
            inputRef={inputRef}
            onChange={(e) => changeSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') changeSearch('');
              if (e.key === 'Enter' && external && searchOnSubmit) onSearchChange(input.trim());
            }}
            sx={{
              width: SEARCH_WIDTH,
              maxWidth: '100%',
              '& .MuiInputBase-root': { pr: 0.5 },
              // The custom Clear adornment replaces WebKit's native one.
              '& input::-webkit-search-cancel-button': { WebkitAppearance: 'none' },
            }}
            // On the user grids the placeholder mentions "Email" — enough for
            // the browser's password manager to autofill the saved console
            // login into it on page load, silently filtering the grid by the
            // current user. type="search" (+ ignore hints) keeps credential
            // autofill out.
            inputProps={{
              type: 'search',
              name: 'grid-quick-search',
              autoComplete: 'off',
              'data-1p-ignore': 'true',
              'data-lpignore': 'true',
              'data-bwignore': 'true',
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color={input ? 'primary' : undefined} />
                </InputAdornment>
              ),
              endAdornment: input ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" onClick={() => changeSearch('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      )}
    </Box>
  );
}
